import { Listbox, Transition, RadioGroup } from "@headlessui/react";
import {
  CheckIcon,
  ExclamationTriangleIcon,
  ChevronUpDownIcon,
  PaperClipIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { getIn } from "formik";
import moment from "moment";
import { Fragment, useRef, useEffect, useState } from "react";
import Mask from "react-input-mask";

import { classNames } from "../../../util";
import formatUpToTwoDecimalPlaces from "../../../util/formatUpToTwoDecimalPlaces";

import { Button } from "../Button";
import { IconButton } from "..";
import { Spinner } from "../Spinner";

import LongTextInput from "./components/LongTextInput";
import RichText from "./components/RichText";
import MultiSelect from "./components/MultiSelect";
import SearchBar from "./components/SearchBar";
import Toggle from "./components/Toggle";
import CKEditorText from "./components/CKEditorText";
import ContactSearch from "./components/ContactSearch";
import InvoiceSearchInput from "./components/InvoiceSearch";
import InstanceSearchInput from "./components/InstanceSearch";
import ExhibitionSearchInput from "./components/ExhibitionSearch";
import ArtfairSearchInput from "./components/ArtfairSearch";
import ARR from "./components/ARR";
import ArtworkSearchInput from "./components/ArtworkSearch";
import ConsignmentSearchInput from "./components/ConsignmentSearch";
import TransactionSearchInput from "./components/TransactionSearch";

import Checkbox from "../Checkbox";
import { Modal } from "../Modal";
import ImageIcon from "../../../assets/icons/ImageIcon";

const useAutosizeTextArea = (textAreaRef, value, rows) => {
  useEffect(() => {
    if (textAreaRef) {
      textAreaRef.style.height = "0px";
      const scrollHeight = textAreaRef.scrollHeight;
      textAreaRef.style.height =
        (scrollHeight > rows * 38 ? scrollHeight : rows * 36) + "px";
    }
  }, [textAreaRef, value]);
};

const Operator = props => {
  const { formik, label, name, operatorOptions, type } = props;

  const options = {
    "multi-select": [
      { label: "In", value: "in" },
      { label: "Not In", value: "notIn" },
      { label: "Is Null", value: "isNull" },
    ],
    number: [
      { label: "Is Null", value: "isNull" },
      { label: "Equal", value: "eq" },
      { label: "Not Equal", value: "ne" },
      { label: "Greater Than", value: "gt" },
      { label: "Less Than", value: "lt" },
    ],
    text: [
      { label: "Is Null", value: "isNull" },
      { label: "Equal", value: "eq" },
      { label: "Not Equal", value: "ne" },
      { label: "Like", value: "like" },
      { label: "Not Like", value: "notLike" },
    ],
    search: [
      { label: "Equal", value: "eq" },
      { label: "Not Equal", value: "ne" },
    ],
  };

  const inputProps = {
    defaultValue: "like",
    formik,
    name,
    options:
      operatorOptions ||
      options[type] ||
      (type.includes("Search") && options["search"]) ||
      options["text"],
    type: "select",
    variant: "small",
  };

  return (
    <div className="flex items-center gap-4">
      <div>{label}</div>
      <div>{getInput(inputProps)}</div>
    </div>
  );
};

const PostLabelInput = props => {
  const { formik, label, postLabelInput } = props;
  const inputProps = {
    ...postLabelInput,
    className: `flex-row-reverse ${postLabelInput?.className}`,
    formik,
  };

  return (
    <div
      className={`flex items-center gap-4 ${postLabelInput && "justify-between"}`}
    >
      <div>{label}</div>
      <div>{postLabelInput && getInput(inputProps)}</div>
    </div>
  );
};

const flattenSelectOptions = (options = []) =>
  options.flatMap(option =>
    Array.isArray(option?.options) ? option.options : [option],
  );

const nonAutosaveInputTypes = ["label", "removeDynamicInputs", "title"];

const isAutosaveField = ({ disabled, formik, name, readOnly, type }) =>
  Boolean(
    formik?.autosave &&
      name &&
      !disabled &&
      !readOnly &&
      !nonAutosaveInputTypes.includes(type),
  );

const getInput = props => {
  const input = renderInput(props);

  if (!isAutosaveField(props)) return input;

  const { autosave } = props.formik;

  return (
    <div
      className="autosave-field contents"
      data-autosave-state={autosave.getFieldState?.(props.name) || "idle"}
      onBlurCapture={() => autosave.saveNow?.()}
    >
      {input}
    </div>
  );
};

const renderInput = props => {
  const {
    allowNegativeInput = true,
    allowDecimal = true,
    formik,
    hasOperator = false,
    label,
    name: defaultName,
    submitOnBlur = false,
    submitOnChange = false,
    onChange,
    options,
    operatorOptions,
    postLabelInput,
    type,
    step = 1,
    min,
    max,
    preSubmit = () => {
      return;
    },
    title,
    emptyToZero = false,
    allowEmpty = false,
    labelClass = "",
    roundToTwoDecimalPlaces = false,
  } = props;
  const name = hasOperator ? `${defaultName}.value` : defaultName;

  const setValue = async value => {
    await formik?.getFieldHelpers(name)?.setValue(value);

    if (!formik?.autosave) {
      await submit();
    }
  };

  const submit = async () => {
    await preSubmit();

    if (submitOnBlur || submitOnChange) {
      return formik?.autosave?.saveNow
        ? formik.autosave.saveNow()
        : formik?.submitForm();
    }
  };

  const operatorProps = {
    formik,
    label,
    name: `${defaultName}.operator`,
    operatorOptions,
    type,
  };

  const fieldProps = formik?.getFieldProps(name);
  const value =
    typeof props.value === "function"
      ? props.value(formik?.values)
      : props.value ?? fieldProps?.value;
  const inputProps = {
    ...props,
    ...fieldProps,
    allowNegativeInput,
    allowDecimal,
    error:
      !props.showErrorOnBlur || getIn(formik?.touched, name)
        ? getIn(formik?.errors, name)
        : undefined,
    label:
      hasOperator && type !== "checkbox" ? (
        <Operator {...operatorProps} />
      ) : postLabelInput ? (
        <PostLabelInput {...props} />
      ) : (
        label
      ),
    name,
    touch: getIn(formik?.touched, name),
    value,
  };
  const handleFormikBlur = event => {
    inputProps?.onBlur?.(event);
    if (submitOnBlur) submit();
  };

  if (
    inputProps?.value === null &&
    ((props.designType === "threeStateCheckbox" && type === "toggle") ||
      type === "select" ||
      type === "multi-select")
  ) {
    // Preserve explicit null selections as well as three-state checkboxes.
  } else if (inputProps?.value === undefined || inputProps?.value === null) {
    inputProps.value = "";
  }

  switch (type) {
    case "arr":
      return <ARR {...inputProps} onChange={setValue} />;
    case "artworkSearch":
      return <ArtworkSearchInput {...inputProps} onChange={setValue} />;
    case "contactSearch":
      return <ContactSearch {...inputProps} onChange={setValue} />;
    case "artFairSearch":
      return <ArtfairSearchInput {...inputProps} onChange={setValue} />;
    case "checkbox":
      return (
        <Checkbox
          {...inputProps}
          submitOnChangeFunc={
            submitOnChange && !formik?.autosave
              ? submit
              : () => {
                  return;
                }
          }
          onBlur={submitOnBlur ? submit : undefined}
        />
      );
    case "ckeditortext":
    default:
      return (
        <CKEditorText
          {...inputProps}
          {...(formik ? { onChange: setValue } : {})}
        />
      );
    case "enhanced-currency":
      return (
        <EnhancedCurrencyInput
          {...inputProps}
          onChange={value => {
            formik.setFieldValue(name, value);
          }}
          onBlur={e => {
            const inputValue = e.target.value;
            if (inputValue === "" && emptyToZero) {
              formik?.setFieldValue(name, 0);
            } else if (inputValue === "" && allowEmpty) {
              formik?.setFieldValue(name, null);
            }
            props.onValueBlur?.(
              inputValue === "" ? null : Number(inputValue.replace(/,/g, "")),
            );
            if (submitOnBlur) submit(e);
          }}
        />
      );
    case "consignmentSearch":
      return <ConsignmentSearchInput {...inputProps} onChange={setValue} />;
    case "date": {
      // eslint-disable-next-line
      const handleBlur = e => {
        if (e?.target?.value === "") {
          e.target.value = null;
        }
        handleFormikBlur(e);
      };

      return (
        <div title={title} className={labelClass}>
          <Input
            {...inputProps}
            type={inputProps?.disabled ? "text" : "date"}
            value={
              inputProps?.value &&
              moment
                .parseZone(inputProps.value)
                .utc()
                .format(
                  inputProps.disabled
                    ? props.disabledDateFormat || "YYYY-MM-DD"
                    : "YYYY-MM-DD",
                )
            }
            onBlur={handleBlur}
          />
        </div>
      );
    }
    case "exhibitionSearch":
      return <ExhibitionSearchInput {...inputProps} onChange={setValue} />;
    case "href":
      // eslint-disable-next-line
      const hrefLink =
        typeof inputProps?.hrefLink === "function"
          ? inputProps?.hrefLink?.(inputProps)
          : inputProps?.hrefLink;
      // eslint-disable-next-line
      const href = inputProps?.value?.trim?.();
      // eslint-disable-next-line
      const disabled =
        typeof inputProps?.disabledHref === "function"
          ? inputProps?.disabledHref?.(inputProps)
          : inputProps?.disabledHref || false;

      // eslint-disable-next-line
      const handleClick = () => {
        if (hrefLink) {
          window.open(hrefLink, "_blank", "noreferrer");
        } else if (href) {
          const formattedHref =
            href.startsWith("http://") || href.startsWith("https://")
              ? href
              : `https://${href}`;

          window.open(formattedHref, "_blank", "noreferrer");
        }
      };

      // eslint-disable-next-line
      const postIcon = (
        <>
          <IconButton
            variant="clean"
            title="Open Link"
            onClick={() => !disabled && handleClick()}
          >
            <ArrowTopRightOnSquareIcon
              className={`h-6 w-6 cursor-pointer ${((!hrefLink && !href) || disabled) && "!cursor-default text-gray-400"}`}
            />
          </IconButton>
        </>
      );

      return (
        <Input
          {...{ ...inputProps, type: "text" }}
          onBlur={submitOnBlur ? submit : undefined}
          postIcon={postIcon}
        />
      );
    case "img":
      return (
        <UploadImage
          {...inputProps}
          {...(formik ? { onChange: setValue } : {})}
        />
      );
    case "instanceSearch":
      return <InstanceSearchInput {...inputProps} onChange={setValue} />;
    case "invoiceSearch":
      return <InvoiceSearchInput {...inputProps} onChange={setValue} />;
    case "label":
      return (
        <h5 className={`pb-4 text-xl font-bold ${inputProps?.className}`}>
          {label}
        </h5>
      );
    case "mask":
      return (
        <InputMask {...inputProps} onBlur={submitOnBlur ? submit : undefined} />
      );
    case "multi-select":
      if (
        inputProps?.appendMissingOption &&
        inputProps?.value &&
        !flattenSelectOptions(options).some(
          option => option.value === inputProps?.value,
        )
      ) {
        options?.push({ label: inputProps?.value, value: inputProps?.value });
      }

      return (
        <div title={title} className={labelClass}>
          <MultiSelect
            {...inputProps}
            options={
              inputProps?.isMulti || !inputProps?.isNullable
                ? options
                : (inputProps?.value || inputProps?.value === 0) && options
                  ? [{ label: "Unselect", value: null }, ...options]
                  : options
            }
            selected={
              inputProps?.isMulti || Array.isArray(inputProps?.value)
                ? inputProps?.value
                : flattenSelectOptions(options).find(
                    option => option.value === inputProps.value,
                  ) || null
            }
            {...(formik ? { onChange: onChange || setValue } : {})}
          />
        </div>
      );
    case "number": {
      const toNumberValue = inputValue =>
        inputValue ? +inputValue : allowEmpty ? null : 0;

      if (roundToTwoDecimalPlaces) {
        return (
          <RoundedNumberInput
            {...inputProps}
            onBlur={(e, { isUnchanged, initialValue }) => {
              formik?.setFieldValue(
                name,
                isUnchanged ? initialValue : toNumberValue(e.target.value),
              );
              submitOnBlur && submit();
            }}
          />
        );
      }

      return (
        <Input
          {...inputProps}
          onBlur={e => {
            formik?.setFieldValue(name, toNumberValue(e.target.value));
            submitOnBlur && submit();
          }}
        />
      );
    }
    case "number-max-min":
      return (
        <Input
          {...inputProps}
          type="number"
          step={step}
          min={min}
          max={max}
          onBlur={e => {
            const inputValue = e.target.value;
            formik?.setFieldValue(name, inputValue ? +inputValue : null);
            submitOnBlur && submit();
          }}
          onChange={e => {
            let { value, min, max } = e.target;
            if (min || max) {
              value = Math.max(
                Number(min),
                Math.min(Number(max), Number(value)),
              );
              formik?.setFieldValue(name, value);
            }
            formik?.setFieldValue(name, value);
          }}
        />
      );
    case "number-currency":
      return (
        <CurrencyInput
          {...inputProps}
          onChange={value => {
            formik.setFieldValue(name, value);
          }}
          onBlur={async (e, { isUnchanged = false } = {}) => {
            const inputValue = e.target.value;
            let nextValue;
            let shouldSetValue = false;
            if (isUnchanged) {
              shouldSetValue = false;
            } else if (inputValue === "" && emptyToZero) {
              nextValue = 0;
              shouldSetValue = true;
            } else if (inputValue === "" && allowEmpty) {
              nextValue = null;
              shouldSetValue = true;
            } else if (inputValue) {
              nextValue = parseFloat(inputValue);
              shouldSetValue = true;
            }

            if (shouldSetValue) {
              await formik?.setFieldValue(name, nextValue, false);
            }
            await props.onValueBlur?.(
              shouldSetValue ? nextValue : inputProps.value,
            );
            if (formik?.setFieldTouched) {
              await formik.setFieldTouched(name, true, true);
            } else {
              inputProps.onBlur?.(e);
            }
            submitOnBlur && submit();
          }}
        />
      );
    case "dimension":
      return (
        <DimensionInput
          {...inputProps}
          allowNegativeInput={false}
          onBlur={async event => {
            const inputValue = event.target.value;

            if (inputValue === "" && emptyToZero) {
              formik?.setFieldValue(name, 0);
            } else if (inputValue === "" && allowEmpty) {
              formik?.setFieldValue(name, null);
            } else {
              inputValue && formik?.setFieldValue(name, parseFloat(inputValue));
            }

            await props?.onBlur?.(event);
            submitOnBlur && submit();
          }}
        />
      );
    case "radio":
      return (
        <Radio {...inputProps} {...(formik ? { onChange: setValue } : {})} />
      );
    case "removeDynamicInputs":
      // eslint-disable-next-line
      const deleteModalProps = {
        title: "Are you sure?",
        scale: "sm",
        description: `Would you like to remove this row?`,
        closeOnBackdrop: true,
        disabled: false,
        renderBody: ({ closeModal }) => {
          const handleDelete = async () => {
            const initialValues = formik?.values[name] || [];
            const deleteIndex =
              props?.deleteIndex !== undefined
                ? props.deleteIndex
                : initialValues.length - 1;
            const removedObj = initialValues[deleteIndex];
            const nextValues = initialValues.filter(
              (_, index) => index !== deleteIndex,
            );

            const filteredValues = Object.fromEntries(
              Object.entries(formik?.values).filter(
                ([key]) =>
                  !(
                    props?.deleteKeys || Object.keys(removedObj || {})
                  ).includes(key),
              ),
            );

            const nextFormikValues = {
              ...filteredValues,
              [name]: nextValues,
            };
            await formik.setValues(nextFormikValues);
            await formik.validateForm(nextFormikValues);
            closeModal();
          };
          return (
            <div className="mt-4 flex justify-center">
              <Button
                className="mr-3"
                label="Cancel"
                onClick={() => {
                  closeModal();
                }}
              />
              <Button label="Remove" onClick={handleDelete} />
            </div>
          );
        },
      };
      return (
        <div className="user-list-actions flex text-right text-sm font-medium">
          {!deleteModalProps.disabled && (
            <div
              className={
                props.actionClassName ||
                "mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:grid-cols-4"
              }
            >
              {
                <Modal {...deleteModalProps}>
                  <IconButton variant="clean" title="Remove Row">
                    <XMarkIcon
                      className={`h-4 w-4 cursor-pointer text-gray-400`}
                    />
                  </IconButton>
                </Modal>
              }
            </div>
          )}
        </div>
      );
    case "richtext":
      return (
        <RichText {...inputProps} {...(formik ? { onChange: setValue } : {})} />
      );
    case "select":
      return (
        <InputSelect
          {...inputProps}
          selected={options?.find(option => option.value === inputProps.value)}
          {...(formik ? { onChange: setValue } : {})}
        />
      );
    case "text":
      return <Input {...inputProps} onBlur={handleFormikBlur} />;
    case "text-3decimal":
      return (
        <Input
          {...{ ...inputProps, type: "text" }}
          value={inputProps?.value ? (+inputProps?.value).toFixed(3) : "0.000"}
          onBlur={submitOnBlur ? submit : undefined}
        />
      );
    case "text-currency":
      return (
        <Input
          {...{ ...inputProps, type: "text" }}
          value={
            inputProps?.value
              ? (+inputProps?.value).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "0.00"
          }
          onBlur={submitOnBlur ? submit : undefined}
        />
      );
    case "long-text":
      return <LongTextInput {...inputProps} onBlur={handleFormikBlur} />;
    case "textarea":
      return (
        <TextArea
          {...inputProps}
          onBlur={async event => {
            const inputValue = event.target.value;

            if (String(inputValue)?.trim() === "") {
              if (allowEmpty) {
                formik?.setFieldValue(name, "");
              }
            } else {
              formik?.setFieldValue(name, inputValue);
            }

            await props?.onBlur?.(event);
            submitOnBlur && submit();
          }}
        />
      );
    case "time":
      // eslint-disable-next-line
      let value = inputProps?.value;
      if (inputProps?.value?.includes("T")) {
        const localeTime = new Date(inputProps?.value)
          ?.toTimeString()
          ?.split(":");
        value = `${localeTime[0]}:${localeTime[1]}`;
      }
      return <Input {...inputProps} value={value} onBlur={handleFormikBlur} />;
    case "timestamp":
      return (
        <Input
          {...{ ...inputProps, type: "datetime-local" }}
          value={
            inputProps?.value &&
            moment.utc(inputProps?.value).format("YYYY-MM-DD HH:mm:ss")
          }
          onBlur={submitOnBlur ? submit : undefined}
        />
      );
    case "transactionSearch":
      return <TransactionSearchInput {...inputProps} onChange={setValue} />;
    case "toggle": {
      const { toggleOnValue, toggleOffValue } = props;
      const useNumericToggle =
        toggleOnValue !== undefined && toggleOffValue !== undefined;
      const toggleProps = useNumericToggle
        ? {
            ...inputProps,
            value: inputProps.value === toggleOnValue,
            onChange: nextChecked =>
              setValue(nextChecked ? toggleOnValue : toggleOffValue),
          }
        : { ...inputProps, ...(formik ? { onChange: setValue } : {}) };
      return <Toggle {...toggleProps} />;
    }
  }
};

const UploadImage = props => {
  const {
    accept = "image/*",
    disabled,
    handleDelete,
    name,
    label,
    value,
    onChange,
    handleDetach,
    isMultiSelect = false,
    maxSize,
    uploadType = "image",
    w = "w-96",
    h = "h-32",
    text = `Browse ${uploadType} to upload`,
    className = "",
    textClassName = "",
    uploadButtonClassName = "",
    variant,
    uploadModalTitle = "",
    loading,
    preview = true,
  } = props || {};

  const [previewImage, handlePreviewImage] = useState();
  const [imgFiles, setImgFiles] = useState([]);

  const ref = useRef();

  const handleClick = () => {
    ref?.current?.click();
  };

  let sumSize = 0;

  if (Array.isArray(value)) {
    sumSize = (
      value?.reduce((acc, cur) => acc + cur.size, 0) /
      (1024 * 1024)
    ).toFixed(2);
  }

  const renderUploadModal = ({ closeModal }) => {
    return (
      <div className="w-full p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-1 flex-col text-2xl font-bold">
            {uploadModalTitle || "Upload Image"}
          </div>
          <div className="flex flex-col">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                action="default"
                label="Cancel"
                onClick={() => {
                  closeModal();
                }}
              />
              <Button
                label={loading ? "Uploading" : "Upload"}
                disabled={loading || !previewImage}
                onClick={() => onChange(ref?.current, closeModal)}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-center pb-3 pt-8">
          {uploader(
            true,
            "w-full max-w-[400px]",
            "aspect-square h-auto max-h-[400px]",
          )}
        </div>
      </div>
    );
  };

  const deleteProfileImageModalProps = {
    title: "Delete Image",
    scale: "sm",
    description: "Are you sure you want to delete this image?",
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const deleteProfileImage = () => handleDelete();

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={"Cancel"}
            action={"default"}
            onClick={() => {
              closeModal?.();
            }}
          />
          <Button
            label={loading ? "Deleting" : "Delete"}
            disabled={loading}
            onClick={deleteProfileImage}
          />
        </div>
      );
    },
  };

  const uploadPreview = () => {
    const file = ref?.current?.files[0];
    const url = URL.createObjectURL(file);
    handlePreviewImage(url);
  };

  const uploadModalProps = {
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: true,
    onOpen: () => handlePreviewImage(null),
    renderBody: renderUploadModal,
  };

  const uploadButton = (
    <Button
      className={`z-10 ${uploadButtonClassName}`}
      label={"Upload"}
      onClick={handleClick}
    />
  );

  const variants = {
    default: {
      withAction: true,
      actionContent: uploadButton,
    },
    modal: {
      withAction: false,
      actionContent: (
        <div className="z-20 flex flex-col items-center gap-4">
          {!disabled && (
            <>
              <Modal {...uploadModalProps}>
                <Button
                  className={`${uploadButtonClassName}`}
                  label={"Upload"}
                  anchorType={true}
                />
              </Modal>
              {handleDelete && value && (
                <Modal {...deleteProfileImageModalProps}>
                  <Button label={"Delete"} />
                </Modal>
              )}
            </>
          )}
        </div>
      ),
    },
  };
  const variantProps = variants[variant] || variants.default;
  const uploader = (uploaderActions, customW, customH) => {
    const wh = `${customW || w} ${customH || h}`;
    const modalPreview = uploaderActions && previewImage;
    return (
      <div
        className={`group relative ${wh} flex-center flex items-center justify-center overflow-hidden border border-black ${className}`}
      >
        {(loading && variantProps.withAction) ||
        (uploaderActions && loading) ? (
          <Spinner className="absolute" />
        ) : modalPreview || value.length > 0 ? (
          preview ? (
            <img
              src={modalPreview ? previewImage : value}
              className="h-auto w-full"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-4">
              {imgFiles.map(imgFile => (
                <div className="flex items-center gap-2" key={imgFile?.name}>
                  <ImageIcon width={24} height={24} />
                  <p>{imgFile?.name}</p>
                </div>
              ))}
            </div>
          )
        ) : (
          !disabled && (
            <span className={`text-sm text-gray-700 ${textClassName}`}>
              {text}
            </span>
          )
        )}
        <div
          className={`opacity-0 ${!disabled ? "cursor-pointer group-hover:opacity-100" : "cursor-default"} absolute inset-0 flex items-center justify-center`}
        >
          <div
            className={`${
              loading ? "opacity-0" : "opacity-70"
            } absolute inset-0 bg-gray-300`}
          />
          <div
            className={`z-10 ${loading ? "pointer-events-none opacity-0" : ""}`}
          >
            {uploaderActions ? uploadButton : variantProps.actionContent}
          </div>
          {(uploaderActions || variantProps.withAction) && (
            <input
              ref={ref}
              type="file"
              className="hidden"
              accept={accept ? accept : "*"}
              onChange={e => {
                if (ref?.current?.files.length > 0) {
                  setImgFiles(Array.from(ref?.current?.files));
                } else {
                  setImgFiles([]);
                }
                uploaderActions ? uploadPreview(e) : onChange(e);
              }}
              multiple={`${isMultiSelect ? "multiple" : ""}`}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col">
      <label htmlFor={name} className="block text-xs font-medium text-black">
        {label}
      </label>
      {uploader()}
      {value && isMultiSelect && handleDetach && (
        <div className="mb-4 mt-4">
          {value?.map((attachment, index) => {
            return (
              <div
                className="flex justify-between px-2 py-1 text-sm"
                key={index}
              >
                <span>{(attachment?.size / (1024 * 1024)).toFixed(2)} MB</span>
                <div className="flex">
                  <PaperClipIcon className="mr-3 inline h-4 w-4" />
                  {attachment?.filename?.split("/").pop()}
                  <XMarkIcon
                    className="ml-8 inline h-4 w-4 cursor-pointer"
                    onClick={() => handleDetach(attachment)}
                  ></XMarkIcon>
                </div>
              </div>
            );
          })}
          {maxSize && (
            <div className="mt-2">
              <IconButton
                title={
                  sumSize > maxSize
                    ? `Max size should be ${maxSize}MB`
                    : `Upto ${maxSize}MB supported`
                }
                className="border-none hover:bg-transparent"
              >
                {sumSize > maxSize ? (
                  <ExclamationTriangleIcon className="mr-4 inline h-5 w-5 text-red-600" />
                ) : (
                  <CheckIcon className="mr-4 inline h-5 w-5 text-green-600" />
                )}
              </IconButton>
              <span>{`${sumSize} size of all attachments (${maxSize} MB max)`}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TextArea = props => {
  const {
    error,
    label,
    subLabel,
    helperText,
    name,
    onChange,
    placeholder,
    type,
    value,
    bottomLabel,
    variant,
    onBlur,
    inputClassName,
    inputContainerClassName = "",
    rows = 3,
    autoHeight,
    disabled = false,
    readOnly = false,
  } = props;

  const textAreaRef = useRef(null);
  useAutosizeTextArea(textAreaRef.current, value || "", rows);

  const inputProps = {
    name,
    onChange,
    placeholder,
    type,
    value: value || "",
    rows,
    disabled,
    readOnly,
    onBlur,
  };

  if (autoHeight) {
    inputProps.ref = textAreaRef;
  }

  const variants = {
    default: {
      container: "flex flex-col flex-grow",
      label: "block text-xs font-medium text-black",
      inputContainer: `${inputContainerClassName}`,
      input: `${disabled ? "!bg-white !text-gray-400 !border-gray-400" : ""} focus:ring-0 focus:border-black block w-full sm:text-sm border-black rounded ${inputClassName}`,
    },
    simpleRight: {
      container: "flex flex-col flex-grow",
      label: `flex flex-row text-xs font-medium text-black ${
        bottomLabel &&
        "order-2 font-bold pt-2 text-right capitalise w-full justify-end"
      }`,
      subLabel: `flex flex-column flex-1 text-left font-light`,
      inputContainer: `flex ${inputContainerClassName}`,
      input: `${inputClassName} ${disabled ? "!bg-white !text-gray-400 !border-gray-400" : ""} w-full border-0 border-b border-black focus:border-black focus:ring-0 pl-0`,
    },
  };

  const classes = variants[variant] || variants.default;

  return (
    <div className={classes.container}>
      <label htmlFor={name} className={classes.label}>
        {subLabel && <span className={classes.subLabel}>{subLabel}</span>}
        <span>{label}</span>
      </label>
      <div className={classes.inputContainer}>
        <textarea {...inputProps} className={classes.input} />
      </div>
      {helperText && (
        <span className="mt-1 text-xs text-gray-500">{helperText}</span>
      )}
      <span className="text-sm text-red-600">{error}</span>
    </div>
  );
};

const Input = props => {
  const {
    preIcon,
    postIcon,
    label,
    labelleft,
    labelleftColor = "black",
    bottomLabel,
    rightLabel,
    name,
    onChange,
    onInput,
    placeholder,
    type,
    value,
    onBlur,
    onFocus,
    onKeyDown,
    inputMode,
    variant = "default",
    className,
    disabled = false,
    error = "",
    inputClassName = "",
    inputContainerClassName = "",
    title,
    step,
    min,
    max,
    maxLength,
    hasActions,
    actionComponent,
    inputBgClass,
  } = props;

  const variants = {
    overlap: {
      container: "relative border border-black rounded px-3 py-2",
      label:
        "absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-medium text-black",
      input: `${
        disabled
          ? `${inputBgClass ?? "!bg-white"} !text-gray-400 !border-gray-400`
          : ""
      } block w-full border-0 p-0 text-black placeholder-gray-500 sm:text-sm leading-5 text-lg focus:border-black focus:ring-0 remove-arrow`,
      inputContainer: `flex ${inputContainerClassName} ${hasActions ? "relative" : ""}`,
      errorMessage: "text-red-600 text-sm",
    },
    default: {
      container: "flex flex-col flex-1",
      label: "block text-xs font-medium text-black mb-1",
      input: `${disabled ? `${inputBgClass ?? "!bg-white"} !text-gray-400 !border-gray-400` : ""} ${
        preIcon ? "rounded-none rounded-r" : "rounded"
      } ${inputClassName} block w-full ${!postIcon ? "border-black" : "border-0"} leading-5 focus:border-black focus:ring-0 remove-arrow`,
      inputContainer: `flex items-center ${inputContainerClassName} ${hasActions ? "relative" : ""} ${postIcon && "border border-black rounded"}`,
      errorMessage: "text-red-600 text-sm",
    },
    horizontal: {
      container:
        "grid grid-cols-1 items-center gap-2 sm:grid-cols-5 sm:gap-x-5",
      label:
        "block text-left sm13 font-medium text-black sm:col-span-1 sm:text-right",
      input: `${disabled ? `${inputBgClass ?? "!bg-white"} !text-gray-400 !border-gray-400` : ""} ${
        preIcon ? "rounded-none rounded-r" : "rounded"
      } border-none font-light text-lightgrey remove-arrow`,
      inputContainer: `flex min-w-0 sm:col-span-3 ${inputContainerClassName} ${hasActions ? "relative" : ""}`,
      errorMessage: "text-sm text-red-600 sm:col-span-3 sm:col-start-2",
    },
    simple: {
      container: "flex flex-col flex-1",
      label: `block text-xs font-medium text-black mb-1 ${
        bottomLabel && "order-2 font-bold pt-2"
      }`,
      input: `${
        disabled
          ? `${inputBgClass ?? "!bg-white"} !text-gray-400 !border-gray-400`
          : ""
      } ${inputClassName} border-0 border-b border-black focus:border-black focus:ring-0 pl-0 remove-arrow`,
      inputContainer: `flex ${inputContainerClassName} ${hasActions ? "relative" : ""}`,
      errorMessage: "text-red-600 text-sm",
    },
    simpleRight: {
      container: "flex flex-col flex-1",
      label: `block text-xs font-medium text-black ${
        bottomLabel && "order-2 font-bold pt-2 text-right"
      }`,
      input: `${
        disabled
          ? `${inputBgClass ?? "!bg-white"} !text-gray-400 !border-gray-400`
          : ""
      } ${inputClassName} w-full border-0 border-b border-black focus:border-black focus:ring-0 pl-0`,
      inputContainer: `flex ${inputContainerClassName} ${hasActions ? "relative" : ""} remove-arrow`,
      errorMessage: "text-red-600 text-sm",
    },
  };
  const classes = variants[variant] || variants.default;

  const inputProps = {
    value: value !== null || value !== undefined ? value : "",
    type,
    name,
    placeholder,
    className: classes.input,
    onBlur,
    onChange,
    onInput,
    disabled,
    onFocus,
    onKeyDown,
    inputMode,
    step,
    min,
    max,
    maxLength,
    title,
  };

  return (
    <div className={`${classes.container} ${className}`}>
      <label htmlFor={name} className={classes.label}>
        {labelleft ? (
          <div className="flex justify-between">
            <div className={`text-${labelleftColor}`}>{labelleft}</div>
            <div>{label}</div>
          </div>
        ) : (
          <div
            className={`${
              rightLabel && "flex flex-row-reverse space-x-4 space-x-reverse"
            }`}
          >
            {label}
          </div>
        )}
      </label>
      <div className={`${bottomLabel && "order-1"} ${classes.inputContainer}`}>
        {preIcon && (
          <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-black px-2 text-white sm:text-sm">
            {preIcon}
          </span>
        )}
        <input {...inputProps} />
        {postIcon && (
          <span className="inline-flex items-center pr-2">{postIcon}</span>
        )}
        {hasActions && (
          <div className="absolute right-1 top-2">{actionComponent}</div>
        )}
      </div>
      {error && <small className={classes.errorMessage}>{error}</small>}
    </div>
  );
};

const EnhancedCurrencyInput = ({
  value,
  onChange,
  onBlur,
  decimalPlaces = 2,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  // When no decimals are allowed the value is treated as a whole number and
  // is always rounded to the nearest integer before it reaches the parent.
  const isInteger = decimalPlaces === 0;

  // Maximum safe value for currency (9.99 trillion)
  // Limited to ensure no precision loss with 2 decimal places
  const MAX_CURRENCY_VALUE = 9999999999999.99;

  // Format number with commas and the configured number of decimal places
  const formatCurrency = num => {
    if (!num && num !== 0) return "";
    return new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(num);
  };

  // Remove formatting to get raw number
  const parseNumber = formattedValue => {
    if (!formattedValue) return "";
    return formattedValue.replace(/[^\d.-]/g, "");
  };

  // Handle input change - no formatting during typing
  const handleChange = e => {
    const input = e.target.value;
    const rawValue = parseNumber(input);

    // Limit input length to prevent overflow (13 digits + decimal point + 2 decimals)
    if (rawValue.length > 16) {
      return;
    }

    // Validate numeric input (allow digits, decimal point, and negative sign)
    if (
      rawValue !== "" &&
      (isNaN(parseFloat(rawValue)) || parseFloat(rawValue) < 0)
    ) {
      return;
    }

    // Check if value exceeds maximum
    const numericValue = parseFloat(rawValue);
    if (numericValue > MAX_CURRENCY_VALUE) {
      return;
    }

    // In integer mode strip any decimal portion so the field only ever shows
    // whole numbers while editing.
    const nextDisplay = isInteger ? rawValue.replace(/\..*$/, "") : rawValue;

    // Store unformatted value while editing
    setDisplayValue(nextDisplay);

    // Call parent onChange with a plain numeric value (rounded when integer)
    if (onChange) {
      if (nextDisplay === "") {
        onChange(null);
      } else {
        const parsed = parseFloat(nextDisplay);
        if (Number.isNaN(parsed)) {
          onChange(null);
        } else {
          onChange(isInteger ? Math.round(parsed) : parsed || null);
        }
      }
    }
  };

  // Handle focus - switch to raw editing mode
  const handleFocus = e => {
    setIsEditing(true);
    const rawValue = parseNumber(displayValue);
    setDisplayValue(rawValue);

    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  // Handle blur - format the value
  const handleBlur = e => {
    setIsEditing(false);
    const rawValue = parseNumber(displayValue);

    if (rawValue !== "" && !isNaN(parseFloat(rawValue))) {
      let numValue = parseFloat(rawValue);

      // Ensure value doesn't exceed maximum
      if (numValue > MAX_CURRENCY_VALUE) {
        numValue = MAX_CURRENCY_VALUE;
      }

      if (isInteger) {
        numValue = Math.round(numValue);
      }

      const formatted = formatCurrency(numValue);
      setDisplayValue(formatted);

      // Update parent with final value
      if (onChange) {
        onChange(numValue);
      }
    } else {
      setDisplayValue("");
      if (onChange) {
        onChange(null);
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  // Update display value when prop value changes (from external source)
  useEffect(() => {
    if (!isEditing) {
      const formatted = value ? formatCurrency(value) : "";
      setDisplayValue(formatted);
    }
  }, [value, isEditing]);

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

/**
 * Number input that displays its value rounded to 2 decimal places (whole
 * numbers without decimals). Blurring without editing reports `isUnchanged`
 * so the caller can keep the precise stored value rather than the rounded one.
 */
const RoundedNumberInput = props => {
  const { value, onBlur, onChange, onFocus } = props;
  const [editing, setEditing] = useState(null);
  const formattedValue = formatUpToTwoDecimalPlaces(value);

  const handleFocus = event => {
    setEditing({
      initialText: formattedValue,
      initialValue: value,
      text: formattedValue,
    });
    onFocus?.(event);
  };

  const handleChange = event => {
    const text = event.target.value;
    setEditing(current => ({ ...current, text }));
    onChange?.(event);
  };

  const handleBlur = event => {
    const isUnchanged =
      editing?.initialText !== undefined &&
      editing.text === editing.initialText;
    setEditing(null);
    onBlur?.(event, { isUnchanged, initialValue: editing?.initialValue });
  };

  return (
    <Input
      {...props}
      type="number"
      value={editing ? editing.text : formattedValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

const CurrencyInput = props => {
  const [currency, setCurrency] = useState();
  const [editingStart, setEditingStart] = useState(null);
  const allowDecimal = props?.allowDecimal || false;
  const allowNegativeInput = props?.allowNegativeInput || false;
  const decimalPlaces = props?.decimalPlaces ?? 2;
  const roundToTwoDecimalPlaces = props?.roundToTwoDecimalPlaces || false;

  useEffect(() => {
    if (roundToTwoDecimalPlaces) {
      if (!editingStart) {
        const hasValue =
          props.value !== undefined &&
          props.value !== null &&
          props.value !== "";
        setCurrency(
          hasValue ? formatUpToTwoDecimalPlaces(props.value) : props.value,
        );
      }
      return;
    }

    if (allowDecimal && props?.decimalPlaces != null && props.value !== "") {
      const hasValue = props.value !== undefined && props.value !== null;
      setCurrency(
        hasValue ? Number(props.value).toFixed(decimalPlaces) : props.value,
      );
      return;
    }

    setCurrency(props.value);
  }, [
    allowDecimal,
    decimalPlaces,
    editingStart,
    props.value,
    props?.decimalPlaces,
    roundToTwoDecimalPlaces,
  ]);

  const handleInputFocus = event => {
    if (roundToTwoDecimalPlaces) {
      setEditingStart({ text: currency, value: props.value });
    }
    props.onFocus?.(event);
  };

  const isAllowedValue = value => {
    const negativePattern = allowNegativeInput ? "-?" : "";
    const numberPattern = allowDecimal ? "\\d*(?:\\.\\d*)?" : "\\d*";

    return new RegExp(`^${negativePattern}${numberPattern}$`).test(value);
  };

  const handleInputChange = event => {
    let newValue = event.target.value;
    if (!isAllowedValue(newValue)) {
      return;
    }

    if (newValue !== "") {
      if (allowDecimal) {
        const valParts = newValue.split(".");
        if (valParts.length > 1 && valParts[1].length > decimalPlaces) {
          newValue = parseFloat(newValue).toFixed(decimalPlaces);
        }
      } else {
        newValue = parseInt(newValue);
      }
      if (!allowNegativeInput && !props?.numericCharactersOnly) {
        newValue = Math.abs(newValue);
      }
    }
    setCurrency(newValue);
    props.onChange?.(newValue);
  };

  const handleInputKeyDown = event => {
    const isExponentOrPositiveSign = ["e", "E", "+"].includes(event.key);
    const isDisallowedDecimal = event.key === "." && !allowDecimal;
    const isDisallowedNegative = event.key === "-" && !allowNegativeInput;

    if (
      isExponentOrPositiveSign ||
      isDisallowedDecimal ||
      isDisallowedNegative
    ) {
      event.preventDefault();
    }
  };

  const handleInputBlur = event => {
    if (roundToTwoDecimalPlaces) {
      const isUnchanged =
        editingStart !== null && currency === editingStart.text;
      setEditingStart(null);
      props.onBlur(event, {
        isUnchanged,
        initialValue: editingStart?.value,
      });
      return;
    }

    if (
      allowDecimal &&
      currency !== "" &&
      currency !== undefined &&
      currency !== null
    ) {
      const roundedValue = parseFloat(currency);
      if (!isNaN(roundedValue)) {
        setCurrency(roundedValue.toFixed(decimalPlaces));
      }
    }
    props.onBlur(event);
  };

  return (
    <Input
      {...props}
      type={props?.numericCharactersOnly ? "text" : "number"}
      inputMode={allowDecimal ? "decimal" : "numeric"}
      value={currency}
      onChange={handleInputChange}
      onFocus={handleInputFocus}
      onKeyDown={handleInputKeyDown}
      onBlur={handleInputBlur}
    />
  );
};

const DimensionInput = props => {
  const { allowDecimal, allowNegativeInput } = props;
  const [value, setValue] = useState();

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const handleInputChange = event => {
    let newValue = event.target.value || "";

    if (newValue !== "") {
      if (allowDecimal && newValue.indexOf(".") !== -1) {
        const [whole, decimal] = newValue.split(".");

        if (whole.length > 1 && decimal.length > 2) {
          newValue = parseFloat(newValue).toFixed(2);
        }
      } else {
        newValue = parseFloat(newValue).toFixed(2);
      }

      if (!allowNegativeInput) {
        newValue = Math.abs(newValue);
      }
    }

    setValue(newValue);
  };

  const handleInputBlur = event => {
    props.onBlur(event);
  };

  return (
    <Input
      {...props}
      type="number"
      value={value}
      onChange={handleInputChange}
      onBlur={handleInputBlur}
    />
  );
};

const InputSelect = props => {
  const {
    className = "",
    disabled = false,
    error,
    selected,
    onChange,
    options = [],
    label,
    variant = "default",
    bottomLabel,
    placeholder,
    rightLabel,
    hideError = false,
    selectedTextColor = "text-black",
    inputContainerClassName = "",
    name,
    onBlur,
  } = props || {};

  const variants = {
    default: {
      label: "block text-xs font-medium text-black ",
      field: `relative ${inputContainerClassName}`,
      button: `bg-white relative w-full border border-black rounded pl-3 pr-10 py-2 text-left cursor-default sm:text-sm ${selectedTextColor}`,
    },
    small: {
      label: "block text-xs font-medium text-black ",
      field: `relative ${inputContainerClassName}`,
      button: `bg-white relative w-full border border-black rounded pl-3 pr-10 text-left cursor-default sm:text-sm ${selectedTextColor}`,
    },
    simple: {
      label: `block text-xs font-medium text-black ${
        bottomLabel && "order-2 font-bold pt-2"
      }`,
      field: `relative ${inputContainerClassName} ${bottomLabel && "order-1"}`,
      button: `relative w-full pr-10 py-2 text-left cursor-default border-b border-black ${selectedTextColor}`,
    },
    simpleRight: {
      label: `block text-xs font-medium text-black ${
        bottomLabel && "order-2 font-bold pt-2 text-right capitalise"
      }`,
      field: `relative ${inputContainerClassName} ${bottomLabel && "order-1"}`,
      button: `relative w-full pr-10 py-2 text-left cursor-default border-b border-black ${selectedTextColor}`,
    },
    horizontal: {
      label: "flex-1 text-left sm13 font-medium sm:col-span-1 sm:text-right",
      field: `relative min-w-0 flex-1 sm:col-span-3 sm:ml-3 ${inputContainerClassName}`,
      container: "grid grid-cols-1 items-center gap-2 sm:grid-cols-5 sm:gap-5",
      button: `relative text-left pr-12 ${selectedTextColor}`,
    },
  };

  const classes = variants[variant] || variants.default;

  return (
    <Listbox
      disabled={disabled}
      value={selected?.value}
      onChange={onChange}
      className={`${classes?.container} ${disabled ? "opacity-50" : ""}`}
    >
      {({ open }) => (
        <div
          className={`${bottomLabel && "flex flex-col"} ${className}`}
          onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              onBlur?.({ target: { name } });
            }
          }}
        >
          <Listbox.Label
            className={`${classes?.label} ${rightLabel && "text-right"}`}
          >
            {label}
          </Listbox.Label>
          <div className={classes?.field}>
            <Listbox.Button className={classes.button}>
              <span className="block truncate">
                {selected?.label || placeholder || "All"}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-black"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full min-w-fit overflow-auto rounded border border-black bg-white py-1 text-base focus:outline-none sm:text-sm">
                {options?.map((item, index) => (
                  <Listbox.Option
                    key={index}
                    disabled={item?.disabled}
                    className={({ active, disabled }) =>
                      classNames(
                        disabled ? "bg-gray-300" : "",
                        active ? "bg-gray-100 text-black" : "text-gray-900",
                        "relative cursor-default select-none py-2 pl-3 pr-9",
                      )
                    }
                    value={item?.value}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? "font-semibold" : "font-normal",
                            "block truncate",
                          )}
                        >
                          {item?.label}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              "absolute inset-y-0 right-0 flex items-center pr-4 text-black",
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
          {error && !hideError && (
            <Listbox.Label
              className={`text-sm text-red-600 ${rightLabel && "text-left"}`}
            >
              {error}
            </Listbox.Label>
          )}
        </div>
      )}
    </Listbox>
  );
};

const Radio = props => {
  const {
    label,
    options = [],
    value,
    onChange,
    variant = "default",
  } = props || {};

  const variants = {
    default: {
      label: "block text-xs font-medium text-black",
      field: "mt-2 relative",
    },
    horizontal: {
      label: "col-span-1 flex-1 sm13 font-medium text-right",
      field: "mt-2 relative flex flex-row justify-between min-w-max",
    },
  };
  const classes = variants[variant] || variants.default;

  return (
    <RadioGroup value={value} onChange={onChange}>
      {label && (
        <RadioGroup.Label className={classes?.label}>{label}</RadioGroup.Label>
      )}
      <div className={classes?.field}>
        {options?.map((item, index) => (
          <RadioGroup.Option
            key={index}
            value={item.value}
            className="cursor-pointer"
          >
            {({ checked }) =>
              checked ? (
                <div className="mb-2 flex items-center">
                  <span className="mr-3 inline-block h-5 w-5 rounded-full border-2 border-black">
                    <CheckIcon className="h-4 w-4" />
                  </span>
                  <span className="text-xs">{item?.label}</span>
                </div>
              ) : (
                <div className="mb-2 flex items-center">
                  <span className="mr-3 inline-block h-5 w-5 rounded-full border-2 border-gray-400" />
                  <span className="text-xs">{item?.label}</span>
                </div>
              )
            }
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
};

const InputMask = props => {
  const {
    preIcon,
    label,
    labelleft,
    labelleftColor = "black",
    bottomLabel,
    name,
    onChange,
    placeholder,
    type,
    value,
    onBlur,
    variant = "default",
    className,
    disabled = false,
    error = "",
    inputClassName = "",
    mask,
  } = props;

  const variants = {
    overlap: {
      container: "relative border border-black rounded px-3 py-2",
      label:
        "absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-medium text-black",
      input:
        "block w-full border-0 p-0 text-black placeholder-gray-500 sm:text-sm leading-5 text-lg focus:border-black focus:ring-0",
      inputContainer: "flex",
    },
    default: {
      container: "flex flex-col flex-1",
      label: "block text-xs font-medium text-black",
      input: `${
        preIcon ? "rounded-none rounded-r" : "rounded"
      } block w-full border-black leading-5 focus:border-black focus:ring-0`,
      inputContainer: "flex",
    },
    horizontal: {
      container: "grid grid-cols-1 items-center gap-2 sm:grid-cols-5 sm:gap-5",
      label:
        "block text-left sm13 font-medium text-black sm:col-span-1 sm:text-right",
      input: `${
        preIcon ? "rounded-none rounded-r" : "rounded"
      } border-none font-light text-lightgrey w-full`,
      inputContainer: "flex min-w-0 sm:col-span-3",
    },
    simple: {
      container: "flex flex-col flex-1",
      label: `block text-xs font-medium text-black ${
        bottomLabel && "order-2 font-bold pt-2"
      }`,
      input: `${inputClassName} border-0 border-b border-black focus:border-black focus:ring-0 pl-0`,
      inputContainer: "flex",
    },
  };
  const classes = variants[variant] || variants.default;

  const inputProps = {
    value: value || "",
    type,
    name,
    placeholder,
    className: classes.input,
    onChange,
    disabled,
    mask,
    onBlur,
  };

  return (
    <div className={`${classes.container} ${className}`}>
      <label htmlFor={name} className={classes.label}>
        {labelleft ? (
          <div className="flex justify-between">
            <div className={`text-${labelleftColor}`}>{labelleft}</div>
            <div>{label}</div>
          </div>
        ) : (
          <div>{label}</div>
        )}
      </label>
      <div className={`${bottomLabel && "order-1"} ${classes.inputContainer}`}>
        {preIcon && (
          <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-black px-2 text-white sm:text-sm">
            {preIcon}
          </span>
        )}
        <Mask
          style={{ outline: "none", marginTop: "10px" }}
          mask={mask}
          {...inputProps}
        />
      </div>
      {error && <small className="text-sm text-red-600">{error}</small>}
    </div>
  );
};

export { SearchBar, Input, getInput, InputSelect };
